import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * Holds open a microphone-typed foreground service for the duration of a
 * consult on Android 14+ (API 34). Without this, the OS silences the
 * MediaRecorder ~10s after the app is backgrounded — fatal for the
 * Siri/Assistant flow where the clinician walks away mid-consult.
 *
 * No-op on iOS (covered by UIBackgroundModes:audio in Info.plist) and on
 * web (PWA can't background-record anyway).
 */

interface MaieForegroundMicPlugin {
  start(): Promise<{ started: boolean }>;
  stop(): Promise<{ stopped: boolean }>;
}

const NativePlugin = registerPlugin<MaieForegroundMicPlugin>(
  "MaieForegroundMic"
);

function isAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export async function startMicForegroundService(): Promise<void> {
  if (!isAndroid()) return;
  try {
    await NativePlugin.start();
  } catch (err) {
    // Don't block the consult if the FGS fails to start — recording will
    // still work in foreground; we just lose the background grace.
    // eslint-disable-next-line no-console
    console.warn("[foregroundMic] start failed", err);
  }
}

export async function stopMicForegroundService(): Promise<void> {
  if (!isAndroid()) return;
  try {
    await NativePlugin.stop();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[foregroundMic] stop failed", err);
  }
}
